import { UpdateTheaterCompanyInput } from "@/graphql/types"
import { InputLabel, TextField } from "@mui/material"
import { useCallback, useMemo, useState } from "react"
import Button from "../common/button/button"
import * as GraphQLClient from "@apollo/client"
import { CreateTheaterCompany, UpdateTheaterCompany } from "@/graphql/documentNode"
import { useSnackBar } from "@/lib/ts/hooks/useSnackBar"
import { useQuery } from "react-query"
import styled from "styled-components"
import { validations } from "../reservation"
import DeleteIcon from "../common/commonIcon/deleteIcon";
import { Authenticator } from "@aws-amplify/ui-react"
import SelectItem from "../common/input/selectItem"

type Props = {
    theaterCompanyData?: UpdateTheaterCompanyInput
    owners?: { name: string; email: string }[]
    onComplete: () => void
}

const CreateOrUpdateTheaterCompany = (props: Props) => {
    const snackBar = useSnackBar({})
    const [emailDuplicateCount, setEmailDuplicateCount] = useState(1)

    const [name, setName] = useState("")
    const [description, setDescription] = useState<string | undefined | null>("")
    const [url, setUrl] = useState<string | undefined | null>("")

    const [emails, setEmails] = useState<string[]>([])
    const [deleteTargetOwners, setDeleteTargetOwners] = useState<string[]>([])

    const nameError = useMemo(() => {
        const texts: string[] = []
        if(!name) {
            texts.push("団体名は必須です")
        } else {
            if(name.length > 50) {
                texts.push("団体名は50文字以下です")
            }
        }
        return texts
    }, [name])

    const descriptionError = useMemo(() => {
        const texts: string[] = []
        if(description && description.length > 100) {
            texts.push("説明は100文字以下です")
        }
        return texts
    }, [description])

    const urlError = useMemo(() => {
        const texts: string[] = []
        if(url) {
            try{
                new URL(url)
            }catch(err) {
                texts.push("URLが無効です")
            }
        }
        return texts
    }, [url])

    const emailError = useCallback((index: number) => {
        const email = emails[index]
        const texts: string[] = []
        if(email) {
            if(!email.match(validations.email)) {
                texts.push("メールアドレスの形式が正しくありません")
            }
        }
        return texts
    }, [emails])

    useQuery([props.theaterCompanyData], () => {
        if(props.theaterCompanyData) {
            setName(props.theaterCompanyData.name)
            setDescription(props.theaterCompanyData.description)
            setUrl(props.theaterCompanyData.url)
        } else {
            setName("")
            setDescription("")
            setUrl("")
        }
    })


    const countUpEmailDuplicate = useCallback(() => {
        if(emailDuplicateCount <= 10) {
            setEmailDuplicateCount(emailDuplicateCount + 1)
            const emailsCopy = [...emails, ""]
            setEmails(emailsCopy)
        }
    }, [emailDuplicateCount, emails])
    const emailInputs = useMemo(() => {
        const inputs: JSX.Element[] = []

        for(let i = 0; i < emailDuplicateCount; i++) {
            inputs.push((
                <div>
                <TextField 
                        sx={{ width: "250px" }}
                        variant="standard"
                        placeholder="https://one-ahead.com"
                        onChange={(event) => {
                            const emailsCopy = [...emails]
                            emailsCopy[i] = event.target.value
                            setEmails(emailsCopy)
                        }}
                        error={!!emailError(i) && emailError(i).length > 0}
                        helperText={emailError(i)}
                />
                </div>
            ))
        }

        return inputs
    }, [emailDuplicateCount, emailError, emails])

    const addDeleteTarget = useCallback((owner: { name: string; email: string }) => {
        setDeleteTargetOwners([...deleteTargetOwners, owner.email])
    }, [deleteTargetOwners])

    const [createTheaterCompany] = GraphQLClient.useMutation(CreateTheaterCompany, {
        variables: {
            input: {
                name,
                description,
                url,
                ownerEmails: emails.filter(email => !!email)
            }
        },
        onCompleted: (data) => {
            snackBar.onMutationSuccess()
        },
        onError: (err) => {
            console.log(err)
            snackBar.onMutationError()
        },
    })

    const [updateTheaterCompany] = GraphQLClient.useMutation(UpdateTheaterCompany, {
        variables: {
            input: {
                id: props.theaterCompanyData?.id,
                name,
                description,
                url,
                ownerEmails: emails.filter(email => !!email)
            }
        },
        onCompleted: (data) => {
            snackBar.onMutationSuccess()
        },
        onError: (err) => {
            snackBar.onMutationError()
        },
    })

    const disableSubmit = useMemo(() => {
        const emailErrors = emails.some((email, index) => !!emailError(index).length)
        return !!nameError.length || !!descriptionError.length || !!urlError.length || emailErrors
    }, [descriptionError.length, emailError, emails, nameError.length, urlError.length])

    const submit = useCallback(async () => {
        if(disableSubmit) {
            return
        }
        snackBar.loadStart()
        console.log(emails)
        if(props.theaterCompanyData) {
            await updateTheaterCompany()
        } else {
            await createTheaterCompany()
        }
        props.onComplete()
    }, [createTheaterCompany, disableSubmit, emails, props, snackBar, updateTheaterCompany])


    return (
        <CreateOrUpdateTheaterCompanyStyle>
            <Authenticator>
                {({ signOut, user }) => (
                <>
                    <div>
                    </div>
                    <div className="input-area">
                        <div className="input">
                            <InputLabel>団体名</InputLabel>
                            <TextField 
                                defaultValue={props.theaterCompanyData?.name}
                                key={props.theaterCompanyData?.name}
                                sx={{ width: "250px" }}
                                variant="standard"
                                placeholder="劇団One-Ahead"
                                onChange={(event) => {
                                    setName(event.target.value);
                                }}
                                error={!!nameError.length}
                                helperText={nameError}
                            />
                        </div>
                        <div className="input">
                            <InputLabel>説明</InputLabel>
                            <TextField 
                                defaultValue={props.theaterCompanyData?.description}
                                key={props.theaterCompanyData?.description}
                                sx={{ width: "250px" }}
                                variant="standard"
                                placeholder="予約システムを運営しています"
                                onChange={(event) => {
                                    setDescription(event.target.value);
                                }}
                                error={!!descriptionError.length}
                                helperText={descriptionError}
                                multiline
                            />
                        </div>
                        <div className="input">
                            <InputLabel>サイト等URL</InputLabel>
                            <TextField 
                                defaultValue={props.theaterCompanyData?.url}
                                key={props.theaterCompanyData?.url}
                                sx={{ width: "250px" }}
                                variant="standard"
                                placeholder="https://one-ahead.com"
                                onChange={(event) => {
                                    setUrl(event.target.value);
                                }}
                                error={!!urlError.length}
                                helperText={urlError}
                            />
                        </div>
                        <div className="input">
                            <InputLabel>管理者メールアドレス</InputLabel>
                            {
                                emailInputs
                            }
                            <div>
                                <Button 
                                    label="メールアドレスをさらに登録"
                                    buttonType="powerful-red"
                                    onClick={countUpEmailDuplicate}
                                    disabled={emailDuplicateCount >= 10}
                                />
                            </div>
                        </div>
                        <div className="input">
                            { props.owners?.length && <InputLabel>登録済管理者</InputLabel> }
                            {
                                props.owners?.map((owner) => (
                                    <div className="existing-owner">
                                        <p>{owner.name}</p>
                                        <div>
                                            <DeleteIcon 
                                                height={40}
                                                width={40}
                                                onClick={() => { addDeleteTarget(owner) }}
                                            />
                                        </div>
                                    </div>
                                )) ?? <></>
                            }
                        </div>
                    </div>
                    <div>
                        <Button 
                            label="送信"
                            buttonType="powerful-red"
                            onClick={submit}
                            disabled={disableSubmit}
                        />
                    </div>
                    {snackBar.component}
                </>
                )}
            </Authenticator>
        </CreateOrUpdateTheaterCompanyStyle>
    )
}

const CreateOrUpdateTheaterCompanyStyle = styled.div`
    
`

export default CreateOrUpdateTheaterCompany